<template>
  <div ref="scrollContainer" class="thread" @scroll="onScroll">
    <Spinner v-if="store.loadingOlder" class="older-loading" />

    <template v-for="day in days" :key="day.label + day.messages[0]?.id">
      <div class="day-divider">
        <Divider />
        {{ day.label }}
        <Divider />
      </div>

      <AgentChatMessage v-for="message in day.messages" :key="message.id" :message="message" />
    </template>

    <div v-if="showIntro" class="intro-row">
      <AgentChatAvatar />
      <ChatBubble direction="incoming" class="intro-bubble">
        This is the same thread as my texts — ask about your leads, tours, listings or applications, or tell me what to
        handle.
      </ChatBubble>
    </div>

    <div v-if="showLoadError" class="load-error">
      <span>Couldn’t load the conversation.</span>
      <Button size="small" variant="outline" @click="refetch()">Retry</Button>
    </div>

    <Spinner v-if="showInitialLoading" class="older-loading" />

    <AgentChatTypingIndicator v-if="store.agentTyping" />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

import { Button, ChatBubble, Divider, Spinner } from '@tallo/design-system';

import { useAgentChatThreadQuery } from '../queries/agent-chat-thread.query';
import { useAgentChatStore } from '../stores/agent-chat.store';
import { groupMessagesByDay } from '../utils/thread-format.utils';
import AgentChatAvatar from './agent-chat-avatar.vue';
import AgentChatMessage from './agent-chat-message.vue';
import AgentChatTypingIndicator from './agent-chat-typing-indicator.vue';

/**
 * Socket pushes are best-effort (an emit only reaches sockets on the emitting API pod), so an
 * open panel revalidates on this interval as the delivery guarantee. Mounted with the thread,
 * so closing the panel is what stops it.
 */
const threadRefreshIntervalMs = 10_000;

const store = useAgentChatStore();

// Mounts with the panel, so every open fetches the newest page (staleTime 0) — a reopened panel
// can't show a stale thread even when every socket push since was missed.
const { data: threadPage, status, refetch } = useAgentChatThreadQuery();

watch(threadPage, (thread) => thread && store.applyThreadPage(thread), { immediate: true });

let refreshTimer: number | undefined;
onMounted(() => {
  refreshTimer = window.setInterval(() => refetch(), threadRefreshIntervalMs);
});
onUnmounted(() => window.clearInterval(refreshTimer));

const scrollContainer = ref<HTMLElement | null>(null);

const days = computed(() => groupMessagesByDay(store.messages));
const threadSettled = computed(() => status.value === 'success' || store.messages.length > 0);
const showIntro = computed(() => threadSettled.value && store.messages.length === 0 && !store.agentTyping);
const showLoadError = computed(() => status.value === 'error' && store.messages.length === 0);
const showInitialLoading = computed(() => status.value === 'pending' && store.messages.length === 0);

const lastMessageId = computed(() => store.messages[store.messages.length - 1]?.id ?? 0);

function scrollToBottom(): void {
  nextTick(() => {
    if (scrollContainer.value) {
      scrollContainer.value.scrollTop = scrollContainer.value.scrollHeight;
    }
  });
}

// New messages and the typing dots pull the thread to the bottom; prepended history must not.
watch([lastMessageId, () => store.agentTyping, threadSettled], scrollToBottom, { immediate: true });

async function onScroll(): Promise<void> {
  const container = scrollContainer.value;

  if (!container || container.scrollTop > 60 || !store.hasMore || store.loadingOlder) {
    return;
  }

  const previousHeight = container.scrollHeight;
  const previousTop = container.scrollTop;

  await store.loadOlderMessages();

  // Keep the viewport anchored on the message the owner was reading, not the new page.
  await nextTick();
  container.scrollTop = container.scrollHeight - previousHeight + previousTop;
}

defineExpose({ scrollToBottom });
</script>

<style scoped lang="scss">
.thread {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.8125rem;
}

.older-loading {
  align-self: center;
  color: var(--t-color-neutral-400);
}

.day-divider {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  color: var(--t-color-neutral-400);
  font-size: 0.6875rem;
  font-weight: 650;
  letter-spacing: 0.04em;
  text-transform: uppercase;

  .t-divider {
    flex: 1;
    width: auto;
  }
}

.intro-row {
  display: flex;
  gap: 0.5625rem;

  .intro-bubble {
    max-width: 85%;
  }
}

.load-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.625rem;
  margin: auto 0;
  color: var(--t-color-neutral-600);
  font-size: 0.84375rem;
}
</style>
